
import { defineComponent } from 'vue'
import ActiveUserMixin from '~/mixins/activeUserMixin'

import AuthMixin from '~/mixins/authMixin'
import ContextMixin from '~/mixins/contextMixin'
import PortalMixin from '~/mixins/portalMixin'
import { TeamPerms } from '~/types/permTypes'
import { TeamMemberRoleChoices } from '~/types/types'

interface ITabsList {
  label: string
  icon: string
  link: string
  validator?: boolean
}

export default defineComponent({
  name: 'DashboardLayout',
  mixins: [AuthMixin, ContextMixin, PortalMixin, ActiveUserMixin],
  head: {
    bodyAttrs: {
      class: 'antialiased stroke-2 hide-scroll'
    }
  },
  async fetch(): Promise<void> {
    await this.fetchActiveUser({})
  },
  computed: {
    isGroupPage(): boolean {
      return Boolean(this.$route.name?.startsWith('orgs-provider-owner-groups'))
    },
    tabsList(): ITabsList[] {
      return [
        {
          label: 'Home',
          icon: 'home',
          link: this.$generateRoute([''])
        },
        {
          label: 'Repositories',
          icon: 'layers',
          link: this.getLink('repos')
        },
        {
          label: 'Reports',
          icon: 'pie-chart',
          link: this.getLink('reports'),
          validator: this.canViewReports
        },
        {
          label: 'Members',
          icon: 'users',
          link: this.getLink('members'),
          validator: this.showTeamMembers
        },
        {
          label: 'Settings',
          icon: 'settings',
          link: this.getLink('settings'),
          validator: this.showTeamSettings
        }
      ] as ITabsList[]
    },
    canViewReports(): boolean {
      return this.$gateKeeper.team(TeamPerms.VIEW_REPORTS, this.teamPerms.permission)
    },
    showTeamMembers(): boolean {
      if (this.activeDashboardContext && 'type' in this.activeDashboardContext) {
        if (this.activeDashboardContext.type === 'user') {
          return false
        }

        const role = this.activeDashboardContext.role as TeamMemberRoleChoices
        return this.$gateKeeper.team(
          [TeamPerms.MANAGE_TEAM_MEMBERS, TeamPerms.VIEW_TEAM_MEMBERS],
          role
        )
      }
      return false
    },
    showTeamSettings(): boolean {
      if (this.activeDashboardContext && 'type' in this.activeDashboardContext) {
        if (this.activeDashboardContext.type === 'user') {
          return false
        }
      }

      if (this.activeDashboardContext && 'role' in this.activeDashboardContext) {
        const role = this.activeDashboardContext.role as TeamMemberRoleChoices
        return this.$gateKeeper.team(
          [
            TeamPerms.CHANGE_PLAN,
            TeamPerms.UPDATE_SEATS,
            TeamPerms.UPDATE_BILLING_DETAILS,
            TeamPerms.MANAGE_TEAM_MEMBERS,
            TeamPerms.VIEW_ACCESS_CONTROL_DASHBOARD,
            TeamPerms.DELETE_TEAM_ACCOUNT,
            TeamPerms.VIEW_TEAM_GENERAL_SETTINGS
          ],
          role
        )
      }

      return false
    }
  },
  methods: {
    getLink(path = ''): string {
      return this.$generateRoute([path], false, true)
    },
    getTabActiveStatus(tab: ITabsList): boolean {
      if (tab.label === 'Home') {
        return this.$route.name === 'provider-owner'
      }

      // Return the top level route path for `settings`
      return this.$route.path.startsWith(
        tab.label === 'Settings' ? this.getLink('settings') : tab.link
      )
    }
  }
})
